import { apiService } from './api';
import { Organization, CreateOrganizationDto, UpdateOrganizationDto, OrganizationQueryParams, OrganizationHierarchy } from '../types/organization';

export class OrganizationService {
  private readonly basePath = '/api/organizations';
  // Simple in-memory cache
  private cache = new Map<string, { data: { data: Organization[]; total: number }, timestamp: number }>();
  private readonly CACHE_TTL = 5 * 60 * 1000; // 5 minutes

  async getOrganizations(params?: OrganizationQueryParams): Promise<{ data: Organization[]; total: number }> {
    const cacheKey = JSON.stringify(params || {});
    const cached = this.cache.get(cacheKey);

    if (cached && (Date.now() - cached.timestamp < this.CACHE_TTL)) {
      return cached.data;
    }

    const response = await apiService.get(this.basePath, { params });
    const result = response.data;

    this.cache.set(cacheKey, {
      data: result,
      timestamp: Date.now()
    });

    return result;
  }

  async getOrganization(id: string): Promise<Organization> {
    const response = await apiService.get(`${this.basePath}/${id}`);
    return response.data;
  }

  async createOrganization(data: CreateOrganizationDto): Promise<Organization> {
    const response = await apiService.post(this.basePath, data);
    this.clearCache();
    return response.data;
  }

  async updateOrganization(id: string, data: UpdateOrganizationDto): Promise<Organization> {
    const response = await apiService.put(`${this.basePath}/${id}`, data);
    this.clearCache();
    return response.data;
  }

  async deleteOrganization(id: string): Promise<void> {
    await apiService.delete(`${this.basePath}/${id}`);
    this.clearCache();
  }

  async mergeOrganizations(sourceId: string, targetId: string, resolvedFields?: Record<string, any>): Promise<Organization> {
    const response = await apiService.post(`${this.basePath}/merge`, {
      sourceId,
      targetId,
      resolvedFields,
    });
    this.clearCache();
    return response.data;
  }

  async getOrganizationHierarchy(id?: string): Promise<OrganizationHierarchy[]> {
    const endpoint = id ? `${this.basePath}/${id}/hierarchy` : `${this.basePath}/hierarchy`;
    const response = await apiService.get(endpoint);
    return response.data;
  }

  async uploadLogo(organizationId: string, file: File): Promise<{ imageUrl: string }> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('entityType', 'organization');
    formData.append('entityId', organizationId);

    const response = await apiService.post('/api/media/upload', formData);
    this.clearCache();
    return response.data;
  }

  private clearCache() {
    this.cache.clear();
  }
}

export const organizationService = new OrganizationService();